import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
/** Maximum size of localStorage in bytes. Deafult is 5MB. */
export const MAX_LOCAL_STORAGE_SIZE = 5242880;
/** Default debounce delay for saving form state to localStorage in milliseconds. */
const DEBOUNCE_DELAY = 200; // ms
/**
 * Helper function to clear the form state from localStorage.
 */
function clearSavedState({ storageKey }) {
    localStorage.removeItem(storageKey);
}
/**
 * Helper function to clear the form state from localStorage.
 */
export function saveState({ storageKey, version, state, }) {
    try {
        const stateWithVersion = Object.assign(Object.assign({}, state), { version });
        const serializedState = JSON.stringify(stateWithVersion);
        if (serializedState.length > MAX_LOCAL_STORAGE_SIZE) {
            console.warn("Form state exceeds localStorage size limit");
            return;
        }
        localStorage.setItem(storageKey, serializedState);
    }
    catch (error) {
        console.error("Error saving form state:", error);
    }
}
/**
 * Helper function to load the form state from localStorage. If the version in localStorage does not match the current version,
 * the saved state will be ignored.
 */
export function loadState({ storageKey, version, onVersionMismatch, }) {
    try {
        const savedState = localStorage.getItem(storageKey);
        if (!savedState)
            return null;
        const parsedState = JSON.parse(savedState);
        if (parsedState.version !== version) {
            console.warn("Form state version mismatch, clearing saved state");
            clearSavedState({ storageKey });
            onVersionMismatch === null || onVersionMismatch === void 0 ? void 0 : onVersionMismatch();
            return null;
        }
        return parsedState;
    }
    catch (error) {
        console.error("Error loading form state:", error);
        clearSavedState({ storageKey });
        return null;
    }
}
/**
 * Helper function to update the form state in localStorage.
 */
function updateState({ storageKey, version, state, onVersionMismatch, }) {
    const savedState = loadState({ onVersionMismatch, storageKey, version }) || {};
    const updatedState = Object.assign(Object.assign({}, savedState), state);
    saveState({ state: updatedState, storageKey, version });
}
export const FormLocalStorageSync = ({ storageKey, debounceDelay = DEBOUNCE_DELAY, version = "1.0", onVersionMismatch, children, }) => {
    /** State to track if the form state has been loaded from localStorage preventing the values flickering. */
    const [isStateLoaded, setIsStateLoaded] = useState(false);
    const { reset, watch, formState, setError, setValue, getValues } = useFormContext();
    // Load initial state
    useEffect(() => {
        const savedState = loadState({ onVersionMismatch, storageKey, version });
        if (savedState) {
            // Reset with all form state data
            reset(savedState.values, {
                keepDirty: true,
                keepDirtyValues: true,
                keepErrors: true,
                keepTouched: true,
            });
            // Manually restore form state
            Object.keys(savedState.errors || {}).forEach((field) => {
                setError(field, savedState.errors[field]);
            });
            // Set touched fields
            Object.keys(savedState.touched || {}).forEach((field) => {
                setValue(field, getValues(field), { shouldTouch: true });
            });
            // Set dirty fields
            Object.keys(savedState.dirty || {}).forEach((field) => {
                setValue(field, getValues(field), { shouldDirty: true });
            });
        }
        setIsStateLoaded(true);
    }, [reset, setError, setValue, getValues, storageKey, version, onVersionMismatch]);
    // Update form state when a field is changed. Debounce to prevent multiple saves. Stable hook that is called once
    // during the first render.
    useEffect(() => {
        const subscription = watch((formValues, { type }) => {
            const timeoutId = setTimeout(() => {
                // We only save if the change was user invoked
                if (type === "change") {
                    updateState({ onVersionMismatch, state: { values: formValues }, storageKey, version });
                }
            }, debounceDelay);
            return () => clearTimeout(timeoutId);
        });
        return () => subscription.unsubscribe();
    }, [watch, debounceDelay, storageKey, version, onVersionMismatch]);
    // Runs only once after the first interaction with a field. The dirt state is changed after the change is propagating using watch.
    useEffect(() => {
        if (formState.isDirty)
            updateState({
                onVersionMismatch,
                state: {
                    dirty: formState.dirtyFields,
                    // We are cleaning the errors stuff that can't be serilized
                    errors: Object.entries(formState.errors).reduce((acc, [field, fieldErrors]) => (Object.assign(Object.assign({}, acc), { [field]: { message: fieldErrors === null || fieldErrors === void 0 ? void 0 : fieldErrors.message, type: fieldErrors === null || fieldErrors === void 0 ? void 0 : fieldErrors.type } })), {}),
                    isDirty: formState.isDirty,
                    touched: formState.touchedFields,
                },
                storageKey,
                version,
            });
    }, [formState, storageKey, version, onVersionMismatch]);
    return isStateLoaded ? children : null;
};
